import { useEffect, useRef, useState } from "react";
import { api, ApiRequestError } from "../api/client";
import { formatRcd, solToLamports } from "../overlay/format";
import { t } from "../strings";
import { signBuilt } from "../wallet/actions";
import { useWallet } from "../wallet/WalletProvider";
import { useGen1Nav } from "./useGen1Nav";

export type RcdTradeSide = "buy" | "sell";

export interface RcdTradeQuote {
  quoteId: string;
  pay: string;
  receive: string;
  minimum: string;
  poolFee: string;
  networkFee: string;
  slippage: string;
  rateLabel: string;
  amountIn: string;
  maxSolReserve?: string;
}

/** Optional design-preview data. When omitted, live /v1/rcd-market quotes are used. */
export interface RcdMarketPreview {
  walletSolLamports: string;
  networkFeeLamports: string;
  rateLabel: string;
  quoteFor: (side: RcdTradeSide, amount: string) => Omit<RcdTradeQuote, "quoteId" | "amountIn" | "rateLabel"> | null;
}

function solAmount(lamports: bigint) {
  const whole = lamports / 1_000_000_000n;
  const fraction = (lamports % 1_000_000_000n)
    .toString()
    .padStart(9, "0")
    .replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

function reasonLabel(reason: string): string {
  switch (reason) {
    case "quote_expired":
      return "QUOTE EXPIRED";
    case "insufficient_balance":
      return t.walletRcdLowBalance;
    case "insufficient_sol":
    case "pool_unavailable":
      return t.walletRcdLowSol;
    case "slippage_exceeded":
      return "PRICE MOVED";
    case "active_session":
      return "CLOSE GAME FIRST";
    default:
      return reason.toUpperCase().replace(/_/g, " ");
  }
}

export function RcdMarket({
  walletRcd,
  walletSolLamports,
  preview,
  onBack,
  onDone,
}: {
  walletRcd: string;
  walletSolLamports?: string;
  preview?: RcdMarketPreview;
  onBack: () => void;
  onDone?: () => void;
}) {
  const rootRef = useRef<HTMLElement>(null);
  const quoteSeq = useRef(0);
  const [view, setView] = useState<"home" | "amount" | "review" | "status">("home");
  const [side, setSide] = useState<RcdTradeSide>("buy");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [snapshot, setSnapshot] = useState<RcdTradeQuote | null>(null);
  const [liveQuote, setLiveQuote] = useState<RcdTradeQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [statusLine, setStatusLine] = useState("");
  const [busy, setBusy] = useState(false);
  const { wallet } = useWallet();
  const solBal = walletSolLamports ?? preview?.walletSolLamports ?? "0";

  const labels =
    view === "home"
      ? ["BUY RCD", "SELL RCD", "BACK"]
      : view === "amount"
        ? ["MAX", "REVIEW", "BACK"]
        : view === "review"
          ? [side === "buy" ? "BUY" : "SELL", "BACK"]
          : ["BACK"];

  useEffect(() => {
    if (preview || view !== "amount") return;
    const seq = ++quoteSeq.current;
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const amountIn =
            side === "buy" ? solToLamports(amount) : /^\d+$/.test(amount) ? amount : "";
          if (!amountIn || BigInt(amountIn) <= 0n) {
            if (seq === quoteSeq.current) setLiveQuote(null);
            return;
          }
          setQuoting(true);
          const q = await api.rcdMarketQuote({ side, amountIn });
          if (seq !== quoteSeq.current) return;
          setLiveQuote({
            quoteId: q.quoteId,
            pay: side === "buy" ? `${amount} SOL` : formatRcd(amountIn),
            receive: side === "buy" ? formatRcd(q.expectedOut) : `${solAmount(BigInt(q.expectedOut))} SOL`,
            minimum: side === "buy" ? formatRcd(q.minimumOutputAmount) : `${solAmount(BigInt(q.minimumOutputAmount))} SOL`,
            poolFee: formatRcd(q.poolFee),
            networkFee: `${solAmount(BigInt(q.networkFeeEstimate))} SOL`,
            slippage: `${(q.slippageBps / 100).toFixed(2)}%`,
            rateLabel: q.rateLabel,
            amountIn,
            maxSolReserve: q.maxSolReserve,
          });
          setError("");
        } catch (e) {
          if (seq !== quoteSeq.current) return;
          setLiveQuote(null);
          setError(e instanceof ApiRequestError ? reasonLabel(e.reason) : t.walletRcdNoQuote);
        } finally {
          if (seq === quoteSeq.current) setQuoting(false);
        }
      })();
    }, 350);
    return () => window.clearTimeout(handle);
  }, [amount, side, view, preview]);

  const quote = preview
    ? (() => {
        try {
          const q = preview.quoteFor(side, amount);
          if (!q) return null;
          return { ...q, quoteId: "preview", amountIn: amount, rateLabel: preview.rateLabel } satisfies RcdTradeQuote;
        } catch {
          return null;
        }
      })()
    : liveQuote;

  const back = () => {
    setError("");
    setStatusLine("");
    if (view === "home" || view === "status") onBack();
    else setView(view === "review" ? "amount" : "home");
  };

  const review = () => {
    try {
      const input =
        side === "buy"
          ? BigInt(solToLamports(amount))
          : /^\d+$/.test(amount)
            ? BigInt(amount)
            : 0n;
      if (input <= 0n) {
        setError(t.walletRcdEnterAmount);
        return;
      }
      if (!quote) {
        setError(quoting ? "…" : t.walletRcdNoQuote);
        return;
      }
      if (side === "sell" && input > BigInt(walletRcd)) {
        setError(t.walletRcdLowBalance);
        return;
      }
      if (side === "buy" && quote.maxSolReserve && BigInt(quote.maxSolReserve) > BigInt(solBal)) {
        setError(t.walletRcdLowSol);
        return;
      }
      setError("");
      setSnapshot(quote);
      setView("review");
    } catch {
      setError(t.walletRcdEnterAmount);
    }
  };

  const confirm = async () => {
    if (preview) {
      setStatusLine(t.walletRcdTradeSoon);
      setView("status");
      return;
    }
    if (!snapshot?.quoteId || snapshot.quoteId === "preview" || !wallet) {
      setError(t.walletRcdNoQuote);
      return;
    }
    setBusy(true);
    setStatusLine("SIGNING…");
    setView("status");
    try {
      const idem = `rcd-${snapshot.quoteId}-${Date.now()}`;
      const built = await api.rcdMarketBuild(snapshot.quoteId, idem);
      setStatusLine("SUBMITTING…");
      const { base64 } = await signBuilt(wallet, built);
      await api.rcdMarketSubmit(built.swapId, base64);
      setStatusLine("CONFIRMED");
      onDone?.();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? reasonLabel(e.reason) : e instanceof Error ? e.message : "FAILED";
      setStatusLine(msg);
    } finally {
      setBusy(false);
    }
  };

  const pick = (index: number) => {
    const label = labels[index];
    if (label === "BACK") back();
    else if (view === "home") {
      setSide(index === 0 ? "buy" : "sell");
      setAmount("");
      setError("");
      setLiveQuote(null);
      setView("amount");
      nav.setIndex(0);
    } else if (label === "MAX") {
      if (side === "buy") {
        const fee = BigInt(preview?.networkFeeLamports ?? "10000000");
        const spendable = BigInt(solBal) - fee;
        setAmount(solAmount(spendable > 0n ? spendable : 0n));
      } else setAmount(walletRcd);
      setError("");
    } else if (label === "REVIEW") review();
    else if (view === "review" && !busy) void confirm();
  };
  const nav = useGen1Nav(labels.length, pick, back);

  useEffect(() => {
    nav.setIndex(0);
    const target =
      rootRef.current?.querySelector<HTMLElement>("input") ??
      rootRef.current?.querySelector<HTMLElement>("button");
    target?.focus({ preventScroll: true });
  }, [view]);

  const title =
    view === "home"
      ? "RCD MARKET"
      : view === "review"
        ? "CONFIRM TRADE"
        : view === "status"
          ? "TRADE"
          : side === "buy"
            ? "BUY RCD"
            : "SELL RCD";
  const balance =
    side === "buy" ? `${solAmount(BigInt(solBal))} SOL` : formatRcd(walletRcd);

  return (
    <section
      ref={rootRef}
      className={`wallet-rcd-market${view === "home" ? " wallet-rcd-market--home" : view === "review" || view === "status" ? " wallet-rcd-market--review" : ""}`}
      aria-label="RCD Market"
    >
      <h2 className="wallet-menu__title">{title}</h2>
      <div className="wallet-rcd-market__body" role="region" aria-label="Trade details" tabIndex={0}>
        {view === "home" ? (
          <>
            <dl className="wallet-menu__kv">
              <dt>RCD</dt>
              <dd>{formatRcd(walletRcd)}</dd>
              <dt>SOL</dt>
              <dd>{solAmount(BigInt(solBal))}</dd>
            </dl>
            <p className="wallet-rcd-market__rate">{quote?.rateLabel ?? preview?.rateLabel ?? t.walletRcdNoQuote}</p>
          </>
        ) : view === "amount" ? (
          <>
            <dl className="wallet-menu__kv">
              <dt>Bal.</dt>
              <dd>{balance}</dd>
            </dl>
            <label className="wallet-market__amount">
              {side === "buy" ? "PAY SOL" : "SELL RCD"}
              <input
                aria-label={side === "buy" ? "SOL to spend" : "RCD to sell"}
                inputMode={side === "buy" ? "decimal" : "numeric"}
                value={amount}
                maxLength={24}
                aria-invalid={!!error}
                onChange={(event) => {
                  setAmount(event.target.value.replace(side === "buy" ? /[^\d.]/g : /\D/g, ""));
                  setError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Backspace") event.stopPropagation();
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                    review();
                  }
                }}
              />
            </label>
            <dl className="wallet-menu__kv wallet-rcd-market__estimate">
              <dt>Get ~</dt>
              <dd>{quoting ? "…" : (quote?.receive ?? "...")}</dd>
            </dl>
            {error && (
              <p className="wallet-rcd-market__error" role="alert">
                {error}
              </p>
            )}
          </>
        ) : view === "status" ? (
          <p className="wallet-rcd-market__rate" role="status">
            {statusLine}
          </p>
        ) : snapshot ? (
          <dl className="wallet-menu__kv">
            <dt>Pay</dt>
            <dd>{snapshot.pay}</dd>
            <dt>Get ~</dt>
            <dd>{snapshot.receive}</dd>
            <dt>Min.</dt>
            <dd>{snapshot.minimum}</dd>
            <dt>LP fee</dt>
            <dd>{snapshot.poolFee}</dd>
            <dt>Net fee</dt>
            <dd>{snapshot.networkFee}</dd>
          </dl>
        ) : null}
      </div>
      <div className="wallet-market__actions" aria-label="Trade actions">
        {labels.map((label, index) => (
          <button key={label} type="button" onFocus={() => nav.setIndex(index)} onClick={() => pick(index)}>
            <span aria-hidden="true">{nav.index === index ? "▶" : "\u00a0"}</span>
            {label}
          </button>
        ))}
      </div>
    </section>
  );
}
